import { h, Fragment, JSX, ComponentChildren } from 'preact'
import { useRef, useState, useEffect } from 'preact/hooks'
import { toArray } from '@nrk/dh-utils'

import styles from './Tabs.module.css'

interface CoreTabsProps extends JSX.HTMLAttributes {
  onChange?: JSX.EventHandler<Event>
}

// Wrapper that handles events and to bypass TypeScript not allowing CustomElements
function CoreTabs({ onChange, ...props }: CoreTabsProps) {
  const ref = useRef<HTMLElement>()
  useEffect(() => {
    if (ref.current && onChange) {
      ref.current.addEventListener('tabs.toggle', onChange)
      return () => {
        if (ref.current) {
          ref.current.removeEventListener('tabs.toggle', onChange)
        }
      }
    }
  }, [ref.current, onChange])
  return h('core-tabs', { ...props, ref })
}

type TabsProps = {
  children: ComponentChildren | ((activeIndex: number) => ComponentChildren)
  headings: ComponentChildren[] | ((activeIndex: number) => ComponentChildren[])
  onChange?: (activeIndex: number) => void
}

export function Tabs(props: TabsProps) {
  const { children, headings: _headings, onChange } = props
  const [activeIndex, setActiveIndex] = useState(0)

  function handleChange(event: Event) {
    if (event.target as HTMLElement) {
      const target = event.target as HTMLElement
      // target.children
      toArray(target.childNodes).forEach((node, idx) => {
        const selected = (node as Element).getAttribute('aria-selected') === 'true'
        if (selected) {
          setActiveIndex(idx)
          if (onChange) {
            onChange(idx)
          }
        }
      })
    }
  }

  const headings = typeof _headings === 'function' ? _headings(activeIndex) : _headings

  return (
    <Fragment>
      <CoreTabs onChange={handleChange} class={styles.tabs}>
        {headings}
      </CoreTabs>
      {children}
    </Fragment>
  )
}

export function TabPanel({ children, ...rest }: JSX.HTMLAttributes) {
  return (
    <div class={styles.tabPanel} {...rest}>
      {children}
    </div>
  )
}
